<template>
  <div>
    <v-row>
      <v-col cols="8" offset="2" >
        <v-card tile flat outlined>
          <v-card-text class="pa-4" style="max-height: 600px; overflow: scroll;">
            <v-form ref="glossaryPhraseForm" >
              <div class="my-1">基本情報</div>
              <v-divider class="my-3"></v-divider>
              <div class="px-5">
                <v-text-field
                  v-model="glossaryPhrase.name"
                  label="用語名"
                  class="mb-3"
                  required
                  :rules="[presenceValidation]"
                >
                  <template v-slot:label>
                    <InputLabel labelName="用語名" :required="true" />
                  </template>
                </v-text-field>
                <v-text-field
                  v-model="glossaryPhrase.readingJa"
                  label="読み仮名（ひらがな）"
                  class="mb-3"
                  required
                  :rules="[presenceValidation, hiraganaOnlyValidation]"
                >
                  <template v-slot:label>
                    <InputLabel labelName="読み仮名（ひらがな）" :required="true" />
                  </template>
                </v-text-field>
                <v-textarea
                  v-model="glossaryPhrase.content"
                  label="用語の説明"
                  class="mb-3"
                >
                  <template v-slot:label>
                    <InputLabel labelName="用語の説明" />
                  </template>
                </v-textarea>
              </div>

              <div class="mt-3 mb-2">リンク設定</div>
              <v-divider class="my-3"></v-divider>
              <div class="px-3">
                <draggable v-model="glossaryPhrase.glossaryPhraseLinks" v-if="glossaryPhrase.glossaryPhraseLinks">
                  <v-card v-for="(link, index) in glossaryPhrase.glossaryPhraseLinks.filter((item) => !item.destroy)" :key="link.priority" tile flat outlined class="mb-3">
                    <template v-if="link.linkType === 'external_link'">
                      <v-card-text class="pa-2">
                        <v-text-field
                          v-model="link.title"
                          label="リンクテキスト"
                          class="mt-5 mb-3"
                          required
                          dense
                          :rules="[presenceValidation]"
                        >
                          <template v-slot:label>
                            <InputLabel labelName="リンクテキスト" :required="true" />
                          </template>
                        </v-text-field>
                        <v-text-field
                          v-model="link.url"
                          label="リンクURL"
                          required
                          dense
                          :rules="[presenceValidation, urlValidation]"
                        >
                          <template v-slot:label>
                            <InputLabel labelName="リンクURL" :required="true" />
                          </template>
                        </v-text-field>
                      </v-card-text>
                      <v-card-actions>
                        <v-btn
                          class="px-2 ml-auto text-caption float-right"
                          color="error darken-1"
                          tile
                          dark
                          depressed
                          small
                          @click="deleteLink(link, index)"
                        >
                          削除する
                        </v-btn>
                      </v-card-actions>
                    </template>
                    <template v-else-if="link.linkType === 'related_post' && link.postId">
                      <v-card-text class="d-flex">
                        <v-avatar class="profile" size="80" tile>
                          <v-img :src="eyecatchableSrc(link.post)" />
                        </v-avatar>
                        <p class="text-body-2 ml-5">
                          {{ link.post.title }}
                        </p>
                      </v-card-text>
                      <v-card-actions class="text-right">
                        <v-btn
                          class="px-2 ml-auto text-caption"
                          color="warning darken-1"
                          tile
                          dark
                          depressed
                          small
                          @click="changeRelatedPost(index)"
                        >
                          変更する
                        </v-btn>
                        <v-btn
                          class="px-2 text-caption"
                          color="error darken-1"
                          tile
                          dark
                          depressed
                          small
                          @click="deleteLink(link, index)"
                        >
                          削除する
                        </v-btn>
                      </v-card-actions>
                    </template>
                  </v-card>
                </draggable>
                <div class="text-center">
                  <v-menu offset-x>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        class="mt-2 mb-2"
                        outlined color="indigo"
                        v-bind="attrs"
                        v-on="on"
                      >
                        リンクを追加する
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item
                        @click="addExternalLink()"
                      >
                        <v-list-item-title>リンク</v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        @click="addRelatedPost()"
                      >
                        <v-list-item-title>関連記事</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </div>
            </v-form>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-switch
              v-model="glossaryPhrase.published"
              class="ml-auto mr-5"
              label="公開する"
            />
            <v-btn
              class="px-6 text-body-2"
              color="primary darken-1"
              min-height="50px"
              max-width="100px"
              tile
              dark
              depressed
              @click="saveGlossaryPhrase"
            >
              保存
            </v-btn>
          </v-card-actions>
        </v-card>
        <post-gallary
          :post-gallary="postGallary"
          @close-post-gallary="postGallary = false"
          @select-post="selectRelatedPost"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import formValidationUtils from '@/mixins/form_validation_utils'
import InputLabel from '@/components/commons/formParts/inputLabel'
import PostGallary from '@/components/commons/PostGallary'
import draggable from 'vuedraggable'
import uuidv4 from 'uuid/v4'

export default {
  name: 'GlossaryPhraseForm',
  components: {
    InputLabel,
    draggable,
    PostGallary
  },
  mixins: [formValidationUtils],
  props: {
    glossaryPhrase: {
      type: Object,
      required: true
    }
  },
  data: () => {
    return {
      postGallary: false,
      postSelectingLinkIndex: null
    }
  },
  methods: {
    eyecatchableSrc (post) {
      if (post.eyecatchable) {
        return post.eyecatchable.file.middle.url
      } else {
        return 'http://placehold.jp/eeeeee/cccccc/200x200.png?text=No%20Image'
      }
    },
    saveGlossaryPhrase () {
      if (!this.$refs.glossaryPhraseForm.validate()) {
        return false
      }

      this.$emit('save-glossary-phrase')
    },
    deleteLink (glossaryPhraseLink) {
      if (glossaryPhraseLink.id) {
        this.glossaryPhrase.glossaryPhraseLinks.find((link) => link.id === glossaryPhraseLink.id).destroy = true
        this.glossaryPhrase.glossaryPhraseLinks.splice()
      } else {
        const index = this.glossaryPhrase.glossaryPhraseLinks.findIndex((link) => link.uuid === glossaryPhraseLink.uuid)
        this.glossaryPhrase.glossaryPhraseLinks.splice(index, 1)
      }
    },
    addExternalLink () {
      this.glossaryPhrase.glossaryPhraseLinks.push({
        uuid: uuidv4(),
        linkType: 'external_link',
        url: null,
        title: null,
        destroy: false,
        priority: this.glossaryPhrase.glossaryPhraseLinks.length
      })
      this.$emit('update-glossary-phrase', this.glossaryPhrase)
    },
    addRelatedPost () {
      this.postGallary = true
      this.postSelectingLinkIndex = this.glossaryPhrase.glossaryPhraseLinks.length
    },
    changeRelatedPost (index) {
      this.postGallary = true
      this.postSelectingLinkIndex = index
    },
    selectRelatedPost (post) {
      if (!this.glossaryPhrase.glossaryPhraseLinks[this.postSelectingLinkIndex]) {
        this.glossaryPhrase.glossaryPhraseLinks.push({
          uuid: uuidv4(),
          linkType: 'related_post',
          post: post,
          postId: post.id,
          destroy: false,
          priority: this.glossaryPhrase.glossaryPhraseLinks.length
        })
      } else {
        this.glossaryPhrase.glossaryPhraseLinks[this.postSelectingLinkIndex].post = post
        this.glossaryPhrase.glossaryPhraseLinks[this.postSelectingLinkIndex].postId = post.id
      }
      this.postSelectingLinkIndex = null
    }
  }
}
</script>

<style scoped>
</style>
